import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, Sparkles, ChevronDown, Play, Lightbulb, Menu, X, MoreHorizontal } from "lucide-react";
import { useAuth } from "../../lib/auth";
import NotificationBell from "../NotificationBell";
import UserMenu from "../UserMenu";
import { TABS, NAV_GROUPS, MODAL_NAV_GROUPS, TOP_LEVEL_TABS } from "../../constants/tabs";

// Lookup helper — `NAV_GROUPS` references tab IDs, but the actual label
// + icon + testid live on `TABS`. Rebuild the full tab object from an ID
// so a rename in one file doesn't silently break the other.
const TABS_BY_ID = Object.fromEntries(TABS.map((t) => [t.id, t]));

/**
 * The top navigation bar — logo, GROUPED tab dropdowns, and the four
 * always-visible action buttons (Urgent / Briefing + notification
 * bell + avatar menu).
 *
 * The 9 previously-flat tabs are now collapsed into 3 dropdown groups
 * (Insights · Data · Ops) so the header stops sprawling as we add
 * more screens. Each group button:
 *   - shows the group's label + a chevron
 *   - highlights when the active tab is one of its children
 *   - opens a small dropdown listing its child tabs on click
 *
 * Purely presentational; parent owns the active-tab + modal state.
 */
export default function AppHeader({ tab, onTabChange, onOpenUrgent, onOpenBriefing, onOpenDemo, onOpenSuggestion }) {
  const { isAdmin } = useAuth();
  const [openGroup, setOpenGroup] = useState(null);
  // Mobile hamburger drawer state. On < md viewports the horizontal
  // nav is hidden and the coach opens this drawer to reach every group
  // + top-level tab + modal. The drawer lives outside the header so it
  // can span the full viewport height without fighting the sticky bar.
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef(null);

  // Close drawer on Escape and on route/tab change.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    document.addEventListener("keydown", onKey);
    // Lock body scroll while the drawer is open so the app underneath
    // doesn't drift as the coach thumbs through the list.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  // Close the dropdown on outside click or Escape. One shared listener
  // covers all three groups because only one dropdown is ever open at
  // a time (opening a second closes the first via `setOpenGroup`).
  useEffect(() => {
    if (!openGroup) return;
    const onDoc = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenGroup(null);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpenGroup(null); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [openGroup]);

  const handleTabPick = (id) => {
    onTabChange(id);
    setOpenGroup(null);
    setMobileOpen(false);
  };

  const handleModalPick = (item) => {
    setOpenGroup(null);
    setMobileOpen(false);
    // Items with a `tab` field switch to that tab (bulk lab imports
    // live inside the Data Editor toolbar — switching tabs is the
    // discoverable behaviour).
    if (item.tab) {
      onTabChange(item.tab);
      return;
    }
    // Otherwise dispatch on the existing `ych:open` bus that UserMenu
    // (always mounted) already subscribes on for HelpAssistant deep-
    // links. Adding a route here means the modal-open logic doesn't
    // need to be lifted out of UserMenu into props.
    window.dispatchEvent(new CustomEvent("ych:open", { detail: { route: item.route } }));
  };

  const TONES = {
    cyan:    "text-cyan-300",
    amber:   "text-amber-300",
    emerald: "text-emerald-300",
    violet:  "text-violet-300",
    rose:    "text-rose-300",
    slate:   "text-slate-300",
  };

  return (
    <header
      className="border-b border-[#1E304B] sticky top-0 z-30 backdrop-blur-xl bg-[#040D1A]/80"
      data-testid="app-header"
      data-no-print="true"
    >
      <div className="px-3 md:px-8 py-3 md:py-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          <img
            src="/branding/favicon-192.png"
            alt="YCH"
            className="w-10 h-10 md:w-16 md:h-16 rounded-xl bg-[#0a1422] border border-cyan-500/40 p-1 object-contain shadow-[0_0_24px_rgba(0,229,255,0.18)] shrink-0"
            data-testid="header-logo"
          />
          <div className="min-w-0">
            <div className="font-display font-extrabold text-sm md:text-lg leading-tight truncate">The Yacht Club of Hyderabad</div>
            <div className="hidden md:block text-[11px] text-slate-400 tracking-wider uppercase">
              Athlete Health · Medical &amp; Blood Parameters
            </div>
          </div>
        </div>

        {/* Mobile-only action strip: hamburger + Urgent + Bell + Avatar.
            Everything else moves into the drawer so the header never
            overflows on a phone. On md+ this whole strip stays hidden
            (its counterpart in the desktop <nav> renders instead). */}
        <div className="flex items-center gap-1 md:hidden">
          <button
            onClick={onOpenUrgent}
            className="pill !p-1.5"
            title="Urgent issues"
            data-testid="open-urgent-btn-mobile"
          >
            <AlertTriangle size={16} className="text-rose-300" />
          </button>
          <NotificationBell />
          <UserMenu />
          <button
            onClick={() => setMobileOpen(true)}
            className="pill !p-1.5"
            data-testid="mobile-menu-btn"
            aria-label="Open main menu"
          >
            <Menu size={18} className="text-cyan-300" />
          </button>
        </div>

        <nav ref={navRef} className="hidden md:flex items-center gap-1 md:gap-2" data-testid="main-nav">
          {NAV_GROUPS.map((g) => {
            const GroupIcon = g.icon;
            const isActiveGroup = g.tabIds.includes(tab) || (g.aliasTabIds || []).includes(tab);
            const isOpen = openGroup === g.id;
            // Show the current sub-tab's label on the pill — when the
            // active tab is an alias (e.g. `sailor` folded into Data
            // Editor), fall back to the group label so we don't
            // advertise the hidden id.
            const activeChild = g.tabIds.includes(tab) ? TABS_BY_ID[tab] : null;
            return (
              <div key={g.id} className="relative">
                <button
                  onClick={() => setOpenGroup(isOpen ? null : g.id)}
                  className={`pill ${isActiveGroup ? "active" : ""}`}
                  data-testid={`nav-group-${g.id}`}
                  aria-expanded={isOpen}
                  title={g.tabIds.map((id) => TABS_BY_ID[id]?.label).filter(Boolean).join(" · ")}
                >
                  <GroupIcon size={14} />
                  {/* On active group show the current sub-tab's name so
                      coaches always see "you're on: Athlete Trend" — on
                      inactive groups show the group label. */}
                  <span className="hidden sm:inline">
                    {activeChild ? activeChild.label : g.label}
                  </span>
                  <ChevronDown
                    size={12}
                    className={`transition-transform ${isOpen ? "rotate-180" : ""} opacity-70`}
                  />
                </button>
                {isOpen && (
                  <div
                    className="absolute right-0 mt-1 min-w-[220px] max-h-[70vh] overflow-y-auto rounded-lg border border-[#1E304B] bg-[#0F1E33] shadow-xl py-1 z-40"
                    data-testid={`nav-group-${g.id}-menu`}
                  >
                    {g.tabIds.map((tid) => {
                      const t = TABS_BY_ID[tid];
                      if (!t) return null;
                      const ItemIcon = t.icon;
                      const isCurrent = tab === tid;
                      return (
                        <button
                          key={tid}
                          onClick={() => handleTabPick(tid)}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-[12.5px] text-left hover:bg-slate-700/30 ${
                            isCurrent ? "text-cyan-300 bg-slate-800/40" : "text-slate-200"
                          }`}
                          data-testid={`tab-${tid}`}
                        >
                          <ItemIcon size={14} className={isCurrent ? "text-cyan-300" : "text-slate-400"} />
                          <span>{t.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {/* Standalone top-level tabs — one-click landing for frequently-
              used screens. Rendered as flat pills (not dropdowns) so the
              coach doesn't have to navigate a group first. */}
          {TOP_LEVEL_TABS.map((tid) => {
            const t = TABS_BY_ID[tid];
            if (!t) return null;
            const TabIcon = t.icon;
            const isCurrent = tab === tid;
            return (
              <button
                key={tid}
                onClick={() => handleTabPick(tid)}
                className={`pill ${isCurrent ? "active" : ""}`}
                data-testid={`tab-${tid}`}
                title={t.label}
              >
                <TabIcon size={14} />
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            );
          })}
          {MODAL_NAV_GROUPS.map((g) => {
            const GroupIcon = g.icon;
            const isOpen = openGroup === g.id;
            const items = g.items.filter((it) => !it.adminOnly || isAdmin);
            if (items.length === 0) return null;
            return (
              <div key={g.id} className="relative">
                <button
                  onClick={() => setOpenGroup(isOpen ? null : g.id)}
                  className="pill"
                  data-testid={`nav-group-${g.id}`}
                  aria-expanded={isOpen}
                  title={items.map((i) => i.label).join(" · ")}
                >
                  <GroupIcon size={14} />
                  <span className="hidden sm:inline">{g.label}</span>
                  <ChevronDown
                    size={12}
                    className={`transition-transform ${isOpen ? "rotate-180" : ""} opacity-70`}
                  />
                </button>
                {isOpen && (
                  <div
                    className="absolute right-0 mt-1 min-w-[240px] rounded-lg border border-[#1E304B] bg-[#0F1E33] shadow-xl py-1 z-40"
                    data-testid={`nav-group-${g.id}-menu`}
                  >
                    {items.map((it) => {
                      const ItemIcon = it.icon;
                      return (
                        <button
                          key={it.id}
                          onClick={() => handleModalPick(it)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-[12.5px] text-left hover:bg-slate-700/30 text-slate-200"
                          data-testid={`nav-modal-${it.id}`}
                          title={it.tooltip || it.label}
                        >
                          <ItemIcon size={14} className={TONES[it.tone] || "text-slate-400"} />
                          <span>{it.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          <div className="ml-1 md:ml-2 flex items-center gap-1 md:gap-2">
            <button
              onClick={onOpenUrgent}
              className="pill"
              title="Top 10 persistent + alarming issues across the squad"
              data-testid="open-urgent-btn"
            >
              <AlertTriangle size={14} className="text-rose-300" />
              <span className="hidden md:inline">Urgent</span>
            </button>
            <button
              onClick={onOpenBriefing}
              className="pill"
              title="Today's AI briefing — Top 3 athletes to check today"
              data-testid="open-briefing-btn"
            >
              <Sparkles size={14} className="text-violet-300" />
              <span className="hidden md:inline">Briefing</span>
            </button>
            {/* "More" dropdown — folds low-frequency actions (Demo, Suggest)
                behind a single pill so the header stops sprawling. Only
                rendered when at least one action is wired. */}
            {((isAdmin && onOpenDemo) || onOpenSuggestion) && (
              <MoreActionsMenu
                open={openGroup === "__more"}
                onToggle={() => setOpenGroup(openGroup === "__more" ? null : "__more")}
                onOpenDemo={isAdmin ? onOpenDemo : null}
                onOpenSuggestion={onOpenSuggestion}
                onPicked={() => setOpenGroup(null)}
              />
            )}
            <NotificationBell />
            <UserMenu />
          </div>
        </nav>
      </div>

      {/* Mobile drawer — a scrollable full-height panel with every tab,
          modal, and action pill grouped by section. Only rendered when
          the coach taps the hamburger; auto-closes on tab pick. */}
      {mobileOpen && (
        <MobileNavDrawer
          tab={tab}
          onClose={() => setMobileOpen(false)}
          onTabPick={handleTabPick}
          onModalPick={handleModalPick}
          onOpenUrgent={() => { setMobileOpen(false); onOpenUrgent?.(); }}
          onOpenBriefing={() => { setMobileOpen(false); onOpenBriefing?.(); }}
          onOpenDemo={onOpenDemo && (() => { setMobileOpen(false); onOpenDemo(); })}
          onOpenSuggestion={onOpenSuggestion && (() => { setMobileOpen(false); onOpenSuggestion(); })}
          isAdmin={isAdmin}
        />
      )}
    </header>
  );
}

/**
 * "More" pill — folds low-frequency actions (Demo tour, Suggest an idea)
 * behind a single dropdown so the top-nav row doesn't overflow. Only
 * items whose handler is supplied are rendered.
 */
function MoreActionsMenu({ open, onToggle, onOpenDemo, onOpenSuggestion, onPicked }) {
  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className="pill"
        title="Demo tour · Share an idea"
        aria-expanded={open}
        data-testid="nav-group-more"
      >
        <MoreHorizontal size={14} className="text-slate-300" />
        <span className="hidden md:inline">More</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""} opacity-70`} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 min-w-[220px] rounded-lg border border-[#1E304B] bg-[#0F1E33] shadow-xl py-1 z-40"
          data-testid="nav-group-more-menu"
        >
          {onOpenDemo && (
            <button
              onClick={() => { onPicked?.(); onOpenDemo(); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-[12.5px] text-left hover:bg-slate-700/30 text-slate-200"
              data-testid="open-demo-btn"
            >
              <Play size={14} className="text-violet-300" />
              <span>Demo tour</span>
            </button>
          )}
          {onOpenSuggestion && (
            <button
              onClick={() => { onPicked?.(); onOpenSuggestion(); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-[12.5px] text-left hover:bg-slate-700/30 text-slate-200"
              data-testid="open-suggestion-btn"
            >
              <Lightbulb size={14} className="text-amber-300" />
              <span>Suggest an idea</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Full-height slide-in drawer for viewports < md. Groups every
 * navigation option (grouped tabs, top-level tabs, modal groups,
 * urgent/briefing/demo/suggestion action pills) into one scrollable
 * column that fits comfortably on a phone screen.
 */
function MobileNavDrawer({
  tab, onClose, onTabPick, onModalPick,
  onOpenUrgent, onOpenBriefing, onOpenDemo, onOpenSuggestion, isAdmin,
}) {
  const TONES = {
    cyan: "text-cyan-300", amber: "text-amber-300", emerald: "text-emerald-300",
    violet: "text-violet-300", rose: "text-rose-300", slate: "text-slate-300",
  };

  return (
    <div
      className="fixed inset-0 z-[300] md:hidden"
      data-testid="mobile-nav-drawer"
    >
      {/* Backdrop — dismisses the drawer on tap-outside. */}
      <div
        className="absolute inset-0 bg-[#040D1A]/85 backdrop-blur-md"
        onClick={onClose}
      />
      <div
        className="absolute inset-y-0 right-0 w-[85vw] max-w-sm bg-[#0B1627] border-l border-[#1E304B] overflow-y-auto shadow-2xl flex flex-col"
        style={{ height: "100vh" }}
      >
        <div className="sticky top-0 bg-[#0B1627] border-b border-[#1E304B] px-4 py-3 flex items-center justify-between">
          <div className="font-display text-base text-slate-100">Menu</div>
          <button
            onClick={onClose}
            className="pill !p-1.5"
            aria-label="Close menu"
            data-testid="mobile-nav-close-btn"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-3 py-3 space-y-4">
          {/* Grouped tabs — Insights / Data / Ops etc. */}
          {NAV_GROUPS.map((g) => {
            const GroupIcon = g.icon;
            return (
              <div key={g.id}>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">
                  <GroupIcon size={11} /> {g.label}
                </div>
                <div className="space-y-1">
                  {g.tabIds.map((tid) => {
                    const t = TABS_BY_ID[tid];
                    if (!t) return null;
                    const ItemIcon = t.icon;
                    const isCurrent = tab === tid;
                    return (
                      <button
                        key={tid}
                        onClick={() => onTabPick(tid)}
                        className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-left border transition ${
                          isCurrent
                            ? "bg-cyan-500/10 border-cyan-500/45 text-cyan-100"
                            : "bg-[#0E1A2E] border-[#1E304B] text-slate-200 hover:border-cyan-500/30"
                        }`}
                        data-testid={`mobile-tab-${tid}`}
                      >
                        <ItemIcon size={14} className={isCurrent ? "text-cyan-300" : "text-slate-400"} />
                        <span>{t.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Standalone top-level tabs. */}
          {TOP_LEVEL_TABS.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Quick tabs</div>
              <div className="space-y-1">
                {TOP_LEVEL_TABS.map((tid) => {
                  const t = TABS_BY_ID[tid];
                  if (!t) return null;
                  const TabIcon = t.icon;
                  const isCurrent = tab === tid;
                  return (
                    <button
                      key={tid}
                      onClick={() => onTabPick(tid)}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-left border transition ${
                        isCurrent
                          ? "bg-cyan-500/10 border-cyan-500/45 text-cyan-100"
                          : "bg-[#0E1A2E] border-[#1E304B] text-slate-200 hover:border-cyan-500/30"
                      }`}
                      data-testid={`mobile-tab-${tid}`}
                    >
                      <TabIcon size={14} className={isCurrent ? "text-cyan-300" : "text-slate-400"} />
                      <span>{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Modal groups (Masters / Bulk / etc). */}
          {MODAL_NAV_GROUPS.map((g) => {
            const GroupIcon = g.icon;
            const items = g.items.filter((it) => !it.adminOnly || isAdmin);
            if (items.length === 0) return null;
            return (
              <div key={g.id}>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">
                  <GroupIcon size={11} /> {g.label}
                </div>
                <div className="space-y-1">
                  {items.map((it) => {
                    const ItemIcon = it.icon;
                    return (
                      <button
                        key={it.id}
                        onClick={() => onModalPick(it)}
                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-left border border-[#1E304B] bg-[#0E1A2E] text-slate-200 hover:border-cyan-500/30 transition"
                        data-testid={`mobile-modal-${it.id}`}
                        title={it.tooltip || it.label}
                      >
                        <ItemIcon size={14} className={TONES[it.tone] || "text-slate-400"} />
                        <span>{it.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Action row — briefing / demo / suggest. Urgent stays in the
              top mobile strip so it's one tap away from anywhere. */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Actions</div>
            <div className="space-y-1">
              <button
                onClick={onOpenBriefing}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-left border border-[#1E304B] bg-[#0E1A2E] text-slate-200 hover:border-violet-500/40 transition"
                data-testid="mobile-open-briefing-btn"
              >
                <Sparkles size={14} className="text-violet-300" /> Briefing
              </button>
              {onOpenSuggestion && (
                <button
                  onClick={onOpenSuggestion}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-left border border-[#1E304B] bg-[#0E1A2E] text-slate-200 hover:border-amber-500/40 transition"
                  data-testid="mobile-open-suggestion-btn"
                >
                  <Lightbulb size={14} className="text-amber-300" /> Suggest an idea
                </button>
              )}
              {isAdmin && onOpenDemo && (
                <button
                  onClick={onOpenDemo}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-left border border-[#1E304B] bg-[#0E1A2E] text-slate-200 hover:border-violet-500/40 transition"
                  data-testid="mobile-open-demo-btn"
                >
                  <Play size={14} className="text-violet-300" /> Demo tour
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
